import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Card,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import KitchenIcon from '@mui/icons-material/Kitchen';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useNavigate } from 'react-router-dom';
import { useGetProductionsQuery } from '../../../store/api/inventoryApi';

function ProductionsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  const { data, isLoading, refetch } = useGetProductionsQuery({
    page: page + 1,
    limit: rowsPerPage,
    search,
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
            }}
          >
            <RestaurantIcon sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
            Production Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/inventory/productions/new')}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
            },
          }}
        >
          New Production Batch
        </Button>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by production number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => refetch()} color="primary">
                <RefreshIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#F8F9FA' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Production Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Recipe (BOM)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Produced Item</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kitchen Location</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Qty Produced</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Total Ingredient Cost</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Logged By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  Loading production records...
                </TableCell>
              </TableRow>
            ) : data?.productions?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  No production records found. Click "New Production Batch" to create one.
                </TableCell>
              </TableRow>
            ) : (
              data?.productions?.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#E65100' }}>
                    {record.productionNumber}
                  </TableCell>
                  <TableCell>
                    {new Date(record.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {record.bom?.name || '—'}
                  </TableCell>
                  <TableCell>
                    {record.product?.name || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<KitchenIcon sx={{ fontSize: '1rem !important' }} />}
                      label={record.location?.name}
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 1.5 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#E65100' }}>
                    {parseFloat(record.quantity).toFixed(1)} unit(s)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {Number(record.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
                  </TableCell>
                  <TableCell>
                    {record.creator?.firstName} {record.creator?.lastName}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data?.pagination?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default ProductionsList;
